/**
 * 基础配置 - 角色权限 接口定义
 */
import { apiDelete, apiGet, apiPost, apiPut } from '@/utils/request';
import { parseListResponse } from '@/utils/apiResponse';

/** 角色基础类型 */
export interface Role {
  id: number;
  type?: string;
  name?: string;
  role_key?: string;
  description?: string;
  /** 是否启用（后端可能返回 enabled 或 status：0=关闭 1=开启） */
  enabled?: boolean;
  status?: number;
  /** 角色类型：1 系统等，后端返回 */
  role_type?: number;
  /** 角色类型文案，如「系统」 */
  role_type_text?: string;
  /** 是否可更新当前角色（后端返回，用于替代前端用 type 判定系统角色不可编辑） */
  is_update?: boolean;
}

/** 创建角色时可选权限项（/roles/selectable 单项） */
export interface RoleSelectableItem {
  id: number;
  name?: string;
  key?: string;
}

/** 可分配权限树节点：/api/roles/available-permissions 返回的 data 项，含 children 递归 */
export interface PermissionTreeNode {
  id: number;
  key?: string;
  name?: string;
  type?: number;
  path?: string | null;
  parent_id?: number | null;
  is_data_permission?: boolean;
  children?: PermissionTreeNode[];
}

/** 将权限树扁平为所有节点 id 列表（用于全选等） */
export function flattenPermissionTreeIds(nodes: PermissionTreeNode[]): number[] {
  const ids: number[] = [];
  function walk(list: PermissionTreeNode[]) {
    for (const n of list) {
      ids.push(n.id);
      if (n.children?.length) walk(n.children);
    }
  }
  walk(nodes);
  return ids;
}

/** 权限树索引：节点 id → 祖先 id 链 / 全部子孙 id */
export interface PermissionTreeIndex {
  ancestors: Map<number, number[]>;
  descendants: Map<number, number[]>;
}

/**
 * 构建权限树索引。
 * 权限树以 check-strictly 方式勾选（父子不联动，才能表达「只给菜单、不给按钮权限」），
 * 父子间的必要约束改由索引在勾选时手动维护。
 */
export function buildPermissionTreeIndex(nodes: PermissionTreeNode[]): PermissionTreeIndex {
  const ancestors = new Map<number, number[]>();
  const descendants = new Map<number, number[]>();

  const walk = (node: PermissionTreeNode, chain: number[]): number[] => {
    ancestors.set(node.id, chain);
    const sub: number[] = [];
    const childChain = [...chain, node.id];
    for (const child of node.children ?? []) {
      sub.push(child.id, ...walk(child, childChain));
    }
    descendants.set(node.id, sub);
    return sub;
  };

  nodes.forEach((n) => walk(n, []));
  return { ancestors, descendants };
}

/** 创建角色请求体：POST /api/roles，application/json，全部必填 */
export interface CreateRolePayload {
  name: string;
  key: string;
  description: string;
  status: number;
  permission_ids: number[];
}

export const rolesApi = {
  list: () => apiGet('/roles').then((d) => parseListResponse<Role>(d)),
  /** 创建角色时可选权限列表：GET /api/roles/selectable，返回 data 为数组或 { list } */
  selectable: () =>
    apiGet('/roles/selectable').then((d) => {
      if (Array.isArray(d)) return d as RoleSelectableItem[];
      const list = (d as { list?: unknown[] })?.list;
      return Array.isArray(list) ? (list as RoleSelectableItem[]) : [];
    }),
  /** 当前用户可分配权限树（菜单含下级按钮）；响应拦截器已返回 res.data.data，故 d 即树数组 */
  availablePermissions: () =>
    apiGet<unknown>('/roles/available-permissions').then((d) =>
      Array.isArray(d) ? (d as PermissionTreeNode[]) : [],
    ),
  /** 获取角色权限：GET /api/roles/{id}/permissions，Query role_type: 1=系统角色 2=自定义角色，editable 是否返回可编辑视图 */
  getPermissions: (id: number, roleType: 1 | 2 = 1, editable = false) =>
    apiGet(`/roles/${id}/permissions`, { role_type: roleType, editable }),
  /** 创建角色：POST /api/roles，Body name、key、description、status、permission_ids 必填 */
  create: (data: CreateRolePayload) => apiPost('/roles', data),
  /** 更新角色基本信息：PUT /api/roles/{id}，Body name、description 可选 */
  update: (id: number, data: { name?: string; description?: string }) =>
    apiPut(`/roles/${id}`, data),
  /** 更新角色权限：PUT /api/roles/{id}/permissions，Body permission_ids 必填，status 可选（仅 role_type=2 可传：1=启用 0=禁用） */
  updatePermissions: (id: number, permissionIds: number[], options?: { status?: 0 | 1 }) =>
    apiPut(`/roles/${id}/permissions`, {
      permission_ids: permissionIds,
      ...(options?.status !== undefined && { status: options.status }),
    }),
  /** 更新角色启用状态：PUT /api/roles/{id}/status，Body status: 1=启用 0=禁用，Query 可选 role_type */
  updateStatus: (id: number, enabled: boolean, roleType?: 1 | 2) =>
    apiPut(`/roles/${id}/status${roleType != null ? `?role_type=${roleType}` : ''}`, {
      status: enabled ? 1 : 0,
    }),
  /** 删除角色：DELETE /api/roles/{id} */
  deleteRole: (id: number) => apiDelete(`/roles/${id}`),
};
